import { useState, useEffect, useCallback, useRef } from "react";
import styled, { createGlobalStyle } from "styled-components";
import { motion, AnimatePresence, useInView } from "framer-motion";
function importAll(r) {
  return r.keys().map(r);
}

const images = importAll(
  require.context("../assets/portfolio", false, /\.(png|jpe?g|webp|JPG|JPEG)$/),
).sort((a, b) => a.localeCompare(b));
// ─── Global ────────────────────────────────────────────────────────────
const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=Jost:wght@200;300;400&display=swap');

  *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }

  :root {
    --cream:      #F7F3EE;
    --cream-dark: #EDE7DC;
    --warm-tan:   #C9AA8C;
    --terracotta: #B87355;
    --mocha:      #7A5C47;
    --ink:        #2E1F14;
    --ink-soft:   #5C4033;
    --gold:       #C49A6C;
    --font-serif: 'Cormorant Garamond', Georgia, serif;
    --font-sans:  'Jost', sans-serif;
  }

  html { scroll-behavior: smooth; }

  body {
    background: var(--cream);
    color: var(--ink);
    font-family: var(--font-sans);
    font-weight: 300;
    -webkit-font-smoothing: antialiased;
    overflow-x: hidden;
    overflow-y: ${({ $locked }) => ($locked ? "hidden" : "auto")};
  }
`;

// ─── Page ──────────────────────────────────────────────────────────────
const Page = styled.div`
  min-height: 100vh;
  background: var(--cream);
`;

// ─── Header ────────────────────────────────────────────────────────────
const Header = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  padding: rem 5vw;
  display: flex;
  display: none;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(
    180deg,
    rgba(247, 243, 238, 0.95) 0%,
    transparent 100%
  );
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
`;

const Logo = styled(motion.a)`
  font-family: var(--font-serif);
  font-size: 1rem;
  font-weight: 300;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  color: var(--ink);
  text-decoration: none;
`;

const BackLink = styled(motion.a)`
  font-family: var(--font-sans);
  font-size: 0.62rem;
  font-weight: 300;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--ink-soft);
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  transition: color 0.3s;
  &:hover {
    color: var(--terracotta);
  }
  svg {
    transition: transform 0.3s;
  }
  &:hover svg {
    transform: translateX(-4px);
  }
`;

// ─── Hero strip ────────────────────────────────────────────────────────
const Hero = styled.div`
  padding: 14rem 5vw 4rem;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 2rem;
  flex-wrap: wrap;
`;

const HeroLeft = styled.div``;

const HeadingWrap = styled.div`
  overflow: hidden;
  margin-bottom: 0.1rem;
`;
const Heading = styled(motion.h1)`
  font-family: var(--font-serif);
  font-size: clamp(3rem, 6vw, 6rem);
  font-weight: 300;
  line-height: 0.9;
  color: var(--ink);
  letter-spacing: -0.02em;
`;
const HeadingItalic = styled(Heading)`
  font-style: italic;
  color: var(--terracotta);
`;

const HeroRight = styled(motion.div)`
  font-family: var(--font-sans);
  font-size: 0.7rem;
  font-weight: 300;
  letter-spacing: 0.06em;
  line-height: 1.8;
  color: var(--ink-soft);
  max-width: 28ch;
  text-align: right;

  @media (max-width: 600px) {
    text-align: left;
  }
`;

const HeroCount = styled(motion.span)`
  display: block;
  font-family: var(--font-serif);
  font-size: 0.8rem;
  font-style: italic;
  color: var(--warm-tan);
  margin-top: 0.4rem;
`;

// ─── Divider ───────────────────────────────────────────────────────────
const Rule = styled(motion.div)`
  height: 1px;
  margin: 0 5vw;
  background: linear-gradient(
    90deg,
    var(--terracotta),
    var(--gold) 40%,
    transparent
  );
  opacity: 0.2;
  transform-origin: left;
`;

// ─── Grid ──────────────────────────────────────────────────────────────
const Grid = styled.div`
  padding: 3rem 5vw 8rem;
  columns: 4 280px;
  column-gap: 8px;

  @media (max-width: 900px) {
    columns: 3 200px;
  }
  @media (max-width: 600px) {
    columns: 2 160px;
  }
`;

const GridItem = styled(motion.div)`
  break-inside: avoid;
  margin-bottom: 8px;
  overflow: hidden;
  cursor: pointer;
  position: relative;
  background: var(--cream-dark);

  img {
    width: 100%;
    height: auto;
    display: block;
    transition: transform 0.65s cubic-bezier(0.22, 1, 0.36, 1);
  }

  &:hover img {
    transform: scale(1.04);
  }
`;

// Hover shade
const ItemShade = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(46, 31, 20, 0.25);
  opacity: 0;
  transition: opacity 0.35s ease;

  ${GridItem}:hover & {
    opacity: 1;
  }
`;

// Expand icon on hover
const ExpandIcon = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 40px;
  height: 40px;
  border: 1px solid rgba(247, 243, 238, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(247, 243, 238, 0.9);
  opacity: 0;
  transition: opacity 0.35s ease;

  ${GridItem}:hover & {
    opacity: 1;
  }
`;

// ─── Lightbox ──────────────────────────────────────────────────────────
const LBBackdrop = styled(motion.div)`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(14, 7, 4, 0.97);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LBImg = styled(motion.img)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
  max-width: 88vw;
  max-height: 88vh;
  width: auto;
  height: auto;
  object-fit: contain;
  z-index: 1001;
  user-select: none;
  -webkit-user-drag: none;
`;

const LBClose = styled(motion.button)`
  position: fixed;
  top: 1.6rem;
  right: 1.8rem;
  z-index: 1002;
  width: 42px;
  height: 42px;
  border: 1px solid rgba(247, 243, 238, 0.15);
  background: transparent;
  color: rgba(247, 243, 238, 0.5);
  cursor: pointer;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    border-color 0.3s,
    color 0.3s;
  &:hover {
    border-color: var(--terracotta);
    color: var(--cream);
  }
`;

const LBArrow = styled(motion.button)`
  position: fixed;
  top: 50%;
  transform: translateY(-50%);
  ${({ $left }) => ($left ? "left: 1.5rem;" : "right: 1.5rem;")}
  z-index: 1002;
  width: 42px;
  height: 42px;
  border: 1px solid rgba(247, 243, 238, 0.1);
  background: transparent;
  color: rgba(247, 243, 238, 0.35);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    border-color 0.3s,
    color 0.3s;
  &:hover {
    border-color: var(--terracotta);
    color: var(--cream);
  }
  &:disabled {
    opacity: 0.08;
    pointer-events: none;
  }

  @media (max-width: 600px) {
    ${({ $left }) => ($left ? "left: 0.6rem;" : "right: 0.6rem;")}
    width: 36px;
    height: 36px;
  }
`;

const LBCounter = styled(motion.div)`
  position: fixed;
  bottom: 1.8rem;
  left: 50%;
  transform: translateX(-50%);
  font-family: var(--font-serif);
  font-size: 0.82rem;
  font-weight: 300;
  font-style: italic;
  color: rgba(247, 243, 238, 0.25);
  z-index: 1002;
  letter-spacing: 0.1em;
  white-space: nowrap;
`;

// ─── Lazy grid item ────────────────────────────────────────────────────
function LazyItem({ src, index, onClick }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "200px 0px" });

  return (
    <GridItem
      ref={ref}
      initial={{ opacity: 0, y: 16 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{
        duration: 0.65,
        delay: (index % 8) * 0.05,
        ease: [0.22, 1, 0.36, 1],
      }}
      onClick={() => onClick(index)}
    >
      <img
        src={src}
        alt={`Portfolio ${index + 1}`}
        loading="lazy"
        decoding="async"
      />
      <ItemShade />
      <ExpandIcon>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path
            d="M1 1h4M1 1v4M11 11H7M11 11V7M11 1H7M11 1V5M1 11h4M1 11V7"
            stroke="currentColor"
            strokeWidth="1.2"
            strokeLinecap="round"
          />
        </svg>
      </ExpandIcon>
    </GridItem>
  );
}

// ─── Lightbox component ────────────────────────────────────────────────
function Lightbox({ index, total, onClose, onPrev, onNext }) {
  const touchStartX = useRef(null);

  // Keyboard navigation
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose, onPrev, onNext]);

  // Touch / swipe
  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) diff > 0 ? onNext() : onPrev();
    touchStartX.current = null;
  };

  return (
    <>
      <LBBackdrop
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        onClick={onClose}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      />

      <AnimatePresence mode="wait">
        <LBImg
          key={index}
          src={images[index]}
          alt={`Portfolio ${index + 1}`}
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.98 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          onClick={(e) => e.stopPropagation()}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          draggable={false}
        />
      </AnimatePresence>

      <LBClose
        onClick={onClose}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        whileHover={{ scale: 1.08 }}
      >
        ✕
      </LBClose>

      <LBArrow
        $left
        disabled={index === 0}
        onClick={(e) => {
          e.stopPropagation();
          onPrev();
        }}
        initial={{ opacity: 0, x: -6 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0 }}
      >
        <svg width="13" height="9" viewBox="0 0 13 9" fill="none">
          <path
            d="M12 4.5H1M5 1L1 4.5 5 8"
            stroke="currentColor"
            strokeWidth="1.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </LBArrow>

      <LBArrow
        disabled={index === total - 1}
        onClick={(e) => {
          e.stopPropagation();
          onNext();
        }}
        initial={{ opacity: 0, x: 6 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0 }}
      >
        <svg width="13" height="9" viewBox="0 0 13 9" fill="none">
          <path
            d="M1 4.5h11M8 1l4 3.5L8 8"
            stroke="currentColor"
            strokeWidth="1.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </LBArrow>

      <LBCounter
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
      </LBCounter>
    </>
  );
}

// ─── Main ──────────────────────────────────────────────────────────────
export default function PortfolioPage() {
  const [lbIndex, setLbIndex] = useState(null);

  const openLb = useCallback((i) => setLbIndex(i), []);
  const closeLb = useCallback(() => setLbIndex(null), []);
  const prevLb = useCallback(() => setLbIndex((i) => Math.max(i - 1, 0)), []);
  const nextLb = useCallback(
    () => setLbIndex((i) => Math.min(i + 1, images.length - 1)),
    [],
  );

  const revealUp = (delay = 0) => ({
    hidden: { y: "105%" },
    show: {
      y: "0%",
      transition: { duration: 0.85, delay, ease: [0.22, 1, 0.36, 1] },
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    document.title = "Portfolio - Laureta Martini";
  }, []);

  return (
    <>
      <GlobalStyle $locked={lbIndex !== null} />

      <Page>
        {/* ── Header ── */}
        <Header>
          <Logo
            href="/"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            Laureta Martini
          </Logo>

          <BackLink
            href="/"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
          >
            <svg width="14" height="8" viewBox="0 0 14 8" fill="none">
              <path
                d="M13 4H1M5 1L1 4l4 3"
                stroke="currentColor"
                strokeWidth="1"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Back
          </BackLink>
        </Header>

        {/* ── Hero ── */}
        <Hero>
          <HeroLeft>
            <HeadingWrap>
              <Heading variants={revealUp(0.1)} initial="hidden" animate="show">
                Selected
              </Heading>
            </HeadingWrap>
            <HeadingWrap>
              <HeadingItalic
                as={motion.h1}
                variants={revealUp(0.22)}
                initial="hidden"
                animate="show"
              >
                Work.
              </HeadingItalic>
            </HeadingWrap>
          </HeroLeft>

          <HeroRight
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
          >
            Bridal · Editorial · Personal · Glam
            <HeroCount>{images.length} photographs</HeroCount>
          </HeroRight>
        </Hero>

        <Rule
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ delay: 0.5, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
        />

        {/* ── Grid ── */}
        <Grid>
          {images.map((src, i) => (
            <LazyItem key={i} src={src} index={i} onClick={openLb} />
          ))}
        </Grid>
      </Page>

      {/* ── Lightbox ── */}
      <AnimatePresence>
        {lbIndex !== null && (
          <Lightbox
            index={lbIndex}
            total={images.length}
            onClose={closeLb}
            onPrev={prevLb}
            onNext={nextLb}
          />
        )}
      </AnimatePresence>
    </>
  );
}
